/**
 * TierGate — Subscription tier & module toggle aware gating components.
 *
 * <TierGate module="accounting">
 *   <AccountingPage />
 * </TierGate>
 *
 * If the org's tier is insufficient → shows an upgrade CTA card.
 * If the admin toggled the module off → renders nothing (clean hide).
 * If both pass → renders children normally.
 */

import type { ReactNode } from "react";
import { ArrowUpCircle, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useTierGate, useFeatureGate } from "@/hooks/useTierGate";
import { MODULE_CONFIG, TIER_DISPLAY, type ToggleableModule } from "@/lib/tier-config";
import { useNavigate } from "react-router-dom";

// ── Module TierGate ──────────────────────────────────────────

interface TierGateProps {
  /** The toggleable module to check */
  module: ToggleableModule;
  children: ReactNode;
  /** Custom fallback when the module is toggled off (default: render nothing) */
  toggledOffFallback?: ReactNode;
  /** Custom fallback when the tier is too low (default: upgrade card) */
  tierFallback?: ReactNode;
}

export function TierGate({
  module,
  children,
  toggledOffFallback = null,
  tierFallback,
}: TierGateProps) {
  const { hasAccess, tierQualifies, toggleEnabled, isLoading, requiredTier } = useTierGate(module);

  if (isLoading) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (hasAccess) return <>{children}</>;

  // Admin toggled it off — clean hide
  if (tierQualifies && !toggleEnabled) return <>{toggledOffFallback}</>;

  // Tier too low — show upgrade prompt
  if (tierFallback) return <>{tierFallback}</>;
  return <UpgradeCard module={module} requiredTier={requiredTier} />;
}

// ── Feature TierGate (non-module) ────────────────────────────

interface FeatureTierGateProps {
  /** The feature key to check */
  feature: string;
  children: ReactNode;
  /** Custom fallback (default: upgrade card) */
  fallback?: ReactNode;
}

export function FeatureTierGate({ feature, children, fallback }: FeatureTierGateProps) {
  const { hasFeature, isLoading, requiredTier } = useFeatureGate(feature);

  if (isLoading) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
    );
  }

  if (hasFeature) return <>{children}</>;

  if (fallback) return <>{fallback}</>;
  return <UpgradeCard featureKey={feature} requiredTier={requiredTier} />;
}

// ── Inline Badge Gate ────────────────────────────────────────

interface TierBadgeProps {
  module?: ToggleableModule;
  feature?: string;
  children: ReactNode;
}

/**
 * Wraps a button or small UI element. When gated, shows a lock icon
 * and subtle tier badge instead of disabling the entire page section.
 */
export function TierBadge({ module, feature, children }: TierBadgeProps) {
  if (module) return <ModuleTierBadge module={module}>{children}</ModuleTierBadge>;
  if (feature) return <FeatureTierBadge feature={feature}>{children}</FeatureTierBadge>;
  return <>{children}</>;
}

function ModuleTierBadge({ module, children }: { module: ToggleableModule; children: ReactNode }) {
  const { hasAccess, requiredTierName } = useTierGate(module);

  return <TierBadgeContent hasAccess={hasAccess} requiredTierName={requiredTierName}>{children}</TierBadgeContent>;
}

function FeatureTierBadge({ feature, children }: { feature: string; children: ReactNode }) {
  const { hasFeature, requiredTierName } = useFeatureGate(feature);

  return <TierBadgeContent hasAccess={hasFeature} requiredTierName={requiredTierName}>{children}</TierBadgeContent>;
}

function TierBadgeContent({
  hasAccess,
  requiredTierName,
  children,
}: {
  hasAccess: boolean;
  requiredTierName: string;
  children: ReactNode;
}) {
  if (hasAccess) return <>{children}</>;

  return (
    <div className="relative inline-flex items-center gap-1.5 opacity-60 cursor-not-allowed">
      <div className="pointer-events-none">{children}</div>
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 gap-0.5 font-normal">
        <Lock className="h-2.5 w-2.5" />
        {requiredTierName}
      </Badge>
    </div>
  );
}

// ── Upgrade CTA Card ─────────────────────────────────────────

interface UpgradeCardProps {
  module?: ToggleableModule;
  featureKey?: string;
  requiredTier: string;
}

function UpgradeCard({ module, featureKey, requiredTier }: UpgradeCardProps) {
  const navigate = useNavigate();
  const tierInfo = TIER_DISPLAY[requiredTier as keyof typeof TIER_DISPLAY];
  const moduleLabel = module ? MODULE_CONFIG[module].label : featureKey ?? "This feature";
  const moduleDescription = module ? MODULE_CONFIG[module].description : "";

  return (
    <div className="flex items-center justify-center p-8">
      <Card className="max-w-md w-full border-dashed">
        <CardHeader className="text-center pb-3">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-lg">{moduleLabel}</CardTitle>
          {moduleDescription && (
            <CardDescription>{moduleDescription}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            This feature is available on the{" "}
            <Badge className={tierInfo?.badgeColor ?? ""}>
              {tierInfo?.icon} {tierInfo?.name}
            </Badge>{" "}
            plan{tierInfo ? ` starting at $${tierInfo.monthlyPrice}/mo` : ""}.
          </p>
          <Button
            onClick={() => navigate("/admin/billing")}
            className="gap-2"
          >
            <ArrowUpCircle className="h-4 w-4" />
            View Plans & Upgrade
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
